import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError, checkForSuperAdmin } from "../../../core/utils";
import {
  TRANSLATION_FILE_TABS,
  TRANSLATION_FILE_TYPE,
  TRANSLATION_UPLOAD_LANGUAGES,
} from "../../../config/constant";
import { TranslationFileManagementWrapper } from "../style/translationFileManagement";

const getTypeFromSearchParams = (searchParams) => {
  const type = Number(searchParams.get("type"));
  if (type === TRANSLATION_FILE_TYPE.WEB) {
    return TRANSLATION_FILE_TYPE.WEB;
  }
  return TRANSLATION_FILE_TYPE.MOBILE;
};

const UploadTranslations = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);

  const translationType = getTypeFromSearchParams(searchParams);

  const listPath = `/upload-translations?type=${translationType}`;
  const platformLabel =
    translationType === TRANSLATION_FILE_TYPE.MOBILE ? "Mobile" : "Web";

  useEffect(() => {
    if (!checkForSuperAdmin()) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleFileChange = (code, file) => {
    setFiles((prev) => {
      const next = { ...prev };
      if (file) {
        next[code] = file;
      } else {
        delete next[code];
      }
      return next;
    });
  };

  const handleSubmit = () => {
    if (loading) return;

    const selectedFiles = Object.entries(files).filter(([, file]) => file);
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one translation file to upload.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    confirmAlert({
      title: "Confirm upload",
      message: "Are you sure you want to upload the selected translation files?",
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: "overlay-custom-class-name",
      buttons: [
        {
          label: "Yes",
          onClick: () => uploadFiles(selectedFiles),
        },
        {
          label: "No",
        },
      ],
    });
  };

  const uploadFiles = (selectedFiles) => {
    const formData = new FormData();
    formData.append("type", translationType);
    selectedFiles.forEach(([code, file]) => {
      formData.append(code, file);
    });

    setLoading(true);
    API.post(APP_URLS.TRANSLATIONS_UPLOAD, formData)
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          toast.success("Translation files uploaded successfully.", {
            position: toast.POSITION.TOP_RIGHT,
          });
          navigate(listPath);
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        toast.error(APIError(error), {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  return (
    <TranslationFileManagementWrapper>
      <div className="mt-2">
        <div
          className="backContainer ms-4"
          onClick={() => navigate(listPath)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              navigate(listPath);
            }
          }}
        >
          <span className="backButton">x</span>
          <span className="px-3 backButton">|</span>
          <span className="backButton">Cancel</span>
        </div>
      </div>
      <div className="headerContainer">
        <h1>Upload Translations</h1>
        <p className="upload-hint">
          Upload JSON files for en, fr, de, es, and nl. Select platform type
          before uploading.
        </p>
      </div>
      <div className="translation-toolbar translation-toolbar--upload px-4">
        <button
          type="button"
          className="upload-btn upload-submit-btn"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Uploading..." : `Upload ${platformLabel}`}
        </button>
        <div
          className="translation-toolbar__type"
          role="tablist"
          aria-label="Translation platform"
        >
          <span className="translation-toolbar__label">Platform</span>
          <div className="translation-type-tabs">
            {TRANSLATION_FILE_TABS.map((tab) => (
              <button
                key={tab.type}
                type="button"
                role="tab"
                aria-selected={translationType === tab.type}
                className={`type-tab ${
                  translationType === tab.type ? "type-tab--active" : ""
                }`}
                onClick={() => setSearchParams({ type: String(tab.type) })}
                disabled={loading}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="row upload-form-section">
        <div className="row d-flex justify-content-center">
          <div className="col-md-8">
            {TRANSLATION_UPLOAD_LANGUAGES.map((lang) => (
              <div className="file-input-row" key={lang.code}>
                <label
                  htmlFor={`file-${lang.code}`}
                  className="form-label fieldLabel"
                >
                  {lang.name} ({lang.code})
                </label>
                <input
                  type="file"
                  id={`file-${lang.code}`}
                  className="form-control"
                  accept=".json,application/json"
                  disabled={loading}
                  onChange={(e) =>
                    handleFileChange(lang.code, e.target.files?.[0] ?? null)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </TranslationFileManagementWrapper>
  );
};

export default UploadTranslations;
